import state, {setState} from './state';
import {lengthBytesUTF8, stringToUTF8Array} from './utils';

export default class Model {
  constructor(instance) {
    this.instance = instance;
    setState(instance.exports.memory.buffer);
  }

  stringToUTF8OnStack(str) {
    const size = lengthBytesUTF8(str) + 1;
    const ret = this.emscriptenStackAlloc(size);
    stringToUTF8Array(str, state['HEAPU8'], ret, size);
    return ret;
  };

  timerCallback(...args) {
    return this.instance.exports['timer_callback'](...args);
  }

  resizePuzzle(...args) {
    return this.instance.exports['resize_puzzle'](...args);
  }

  restorePuzzleSize(...args) {
    return this.instance.exports['restore_puzzle_size'](...args);
  }

  mousedown(...args) {
    return this.instance.exports['mousedown'](...args);
  }

  mouseup(...args) {
    return this.instance.exports['mouseup'](...args);
  }

  mousemove(...args) {
    return this.instance.exports['mousemove'](...args);
  }

  key(...args) {
    return this.instance.exports['key'](...args);
  }

  dlgReturnSVal(...args) {
    this.instance.exports['dlg_return_sval'](...args);
  }

  dlgReturnIVal(...args) {
    return this.instance.exports['dlg_return_ival'](...args);
  }

  command(...args) {
    return this.instance.exports['command'](...args);
  }

  getTextFormat(...args) {
    return this.instance.exports['get_text_format'](...args);
  }

  freeTextFormat(...args) {
    return this.instance.exports['free_text_format'](...args);
  }

  getSaveFile(...args) {
    return this.instance.exports['get_save_file'](...args);
  }

  getGameState(...args) {
    return this.instance.exports['get_game_state'](...args);
  }

  freeSaveFile(...args) {
    return this.instance.exports['free_save_file'](...args);
  }

  loadGame(...args) {
    return this.instance.exports['load_game'](...args);
  }

  prefsLoadCallback(...args) {
    return this.instance.exports['prefs_load_callback'](...args);
  }

  emscriptenStackInit() {
    return this.instance.exports['emscripten_stack_init']();
  }

  emscriptenStackGetFree() {
    return this.instance.exports['emscripten_stack_get_free']();
  }

  emscriptenStackGetBase() {
    return this.instance.exports['emscripten_stack_get_base']();
  }

  emscriptenStackGetEnd() {
    return this.instance.exports['emscripten_stack_get_end']();
  }

  emscriptenStackRestore(a0) {
    return this.instance.exports['_emscripten_stack_restore'](a0);
  }

  emscriptenStackAlloc(a0) {
    return this.instance.exports['_emscripten_stack_alloc'](a0);
  }

  emscriptenStackGetCurrent() {
    return this.instance.exports['emscripten_stack_get_current']();
  }

  init() {
    this.instance.exports['__wasm_call_ctors']();

    const argValues = this.emscriptenStackAlloc(4);
    state['HEAPU32'][argValues >> 2] = 0;

    try {
      const returnCode = this.instance.exports['__main_argc_argv'](0, argValues);
      this.fFlush(0);
      return returnCode === 0;
    } catch (e) {
      if (e instanceof WebAssembly.RuntimeError && this.emscriptenStackGetCurrent() <= 0) {
        console.error('Stack overflow detected. You can try increasing -sSTACK_SIZE (currently set to 65536)');
      }

      throw e;
    }
  }

  fFlush(...args) {
    try {
      this.instance.exports['fflush'](...args);
    } catch {
      // ignore
    }
  }

  growMemory(newSize) {
    const wasmMemory = this.instance.exports.memory;
    const buffer = wasmMemory.buffer;
    const pages = (newSize - buffer.byteLength + 65535) / 65536;

    // round size grow request up to wasm page size (fixed 64KB per spec)
    try {
      wasmMemory.grow(pages); // .grow() takes a delta compared to the previous size
      setState(wasmMemory.buffer);
      return true;
    } catch (e) {
      console.error(`growMemory: Attempted to grow heap from ${buffer.byteLength}, but got error: ${e}`);
      return false;
    }
  }

  static async create(viewModel) {
    const binary = await fetch(`/puzzles/${viewModel.name}.wasm`, { credentials: 'same-origin' });
    const imports = { 'env': viewModel.wasmInterface, 'wasi_snapshot_preview1': viewModel.wasmInterface };
    const instance = (await WebAssembly.instantiateStreaming(binary, imports)).instance;

    return new Model(instance);
  }
}
